/**
 * /settings/tenant — page d'admin tenant (refonte 9.P.3.C pattern Companies R1+R2).
 *
 * Pattern UX commun :
 *   - sticky page header `top-16 lg:top-0` + H1 + subtitle
 *   - section nav left `lg:grid-cols-[14rem_1fr]` (réutilise CompanySectionNav)
 *   - CardSection moderne par section : Infos / Branding / Admin (super_admin)
 *   - sticky save bar bottom (Cancel + Save)
 *   - useUnsavedChangesGuard global dirty (déjà en place depuis 9.P.2.R2)
 *   - allowNavigation() bypass post-save success (déjà en place)
 *   - max-w-screen-xl (settings rest concis, pas full-width tableaux)
 *   - PO décision Q2=b — sections empilées + section nav, **PAS de tabs**
 *
 * Sections :
 *   - Infos tenant (champs disponibles via /v1/auth/me : name, locale,
 *     timezone, currency, logo_url)
 *   - Branding (couleurs CSS + preview live + reset)
 *   - Admin SaaS (super_admin only — chargé via GET /v1/tenants/:id)
 *
 * À la soumission :
 *   - Tous les rôles admin → PATCH /v1/tenants/{tenant.id}
 *   - tenant_admin : payload validé côté API par UpdateTenantSelfSchema
 *   - super_admin  : payload validé par UpdateTenantSchema
 *
 * Optimistic update : on push immédiatement le branding sur :root pendant
 * que le PATCH est en vol, on rollback en cas d'erreur via la valeur
 * branding pré-submit.
 *
 * RBAC client : seuls `tenant_admin` et `super_admin` accèdent.
 */

import type { QuoteDeliveryPolicy } from "@kollectivia/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import { api, ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { applyBranding, type Branding } from "../../lib/branding";
import { toast } from "../../lib/toast";
import { useUnsavedChangesGuard } from "../../lib/unsavedChanges/UnsavedChangesContext";
import { CompanySectionNav } from "../companies/_components/CompanySectionNav";

import { AdminSettingsForm, type AdminSettingsFormState } from "./_AdminSettingsForm";
import { BrandingForm } from "./_BrandingForm";
import { CreateTenantDialog } from "./_parts/CreateTenantDialog";
import { TenantInfoForm, type TenantInfoFormState } from "./_TenantInfoForm";

/** Filtre un branding pour ne garder que les couleurs hex 6-digits valides. */
function sanitizeBranding(b: Branding | null): Branding | null {
  if (!b) return null;
  const out: Branding = {};
  for (const [k, v] of Object.entries(b)) {
    if (k === "logo_url") {
      if (typeof v === "string" && v.length > 0) out.logo_url = v;
      continue;
    }
    if (typeof v === "string" && /^#[0-9a-fA-F]{6}$/.test(v)) {
      (out as Record<string, string>)[k] = v;
    }
  }
  return Object.keys(out).length === 0 ? null : out;
}

function buildSelfPayload(
  info: TenantInfoFormState,
  branding: Branding | null,
): Record<string, unknown> {
  return {
    name: info.name,
    locale: info.locale,
    timezone: info.timezone || undefined,
    currency: info.currency || undefined,
    logo_url: info.logo_url || null,
    branding: sanitizeBranding(branding ? { ...branding, logo_url: undefined } : null),
    ...(info.logo_upload ? { logo_upload: info.logo_upload } : {}),
  };
}

function buildAdminPayload(
  info: TenantInfoFormState,
  branding: Branding | null,
  admin: AdminSettingsFormState,
): Record<string, unknown> {
  return {
    ...buildSelfPayload(info, branding),
    plan: admin.plan,
    status: admin.status,
    trial_ends_at: admin.trial_ends_at || null,
    max_users: admin.max_users,
    max_projects: admin.max_projects,
    max_storage_mb: admin.max_storage_mb,
  };
}

export function SettingsTenantPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const { user, tenant, refresh } = useAuth();
  const queryClient = useQueryClient();

  const isSuperAdmin = user?.role === "super_admin";
  const isAdminAllowed = !!user && (user.role === "tenant_admin" || user.role === "super_admin");

  const [info, setInfo] = useState<TenantInfoFormState | null>(null);
  const [deliveryPolicy, setDeliveryPolicy] = useState<QuoteDeliveryPolicy | null>(null);
  const [deliveryPolicyDirty, setDeliveryPolicyDirty] = useState(false);
  const [branding, setBranding] = useState<Branding | null>(null);
  const [admin, setAdmin] = useState<AdminSettingsFormState | null>(null);

  const [loadingAdmin, setLoadingAdmin] = useState(isSuperAdmin);
  const [submitting, setSubmitting] = useState(false);
  const [creationOuverte, setCreationOuverte] = useState(false);

  // 9.P.2.R2 — dirty tracking. `isHydrated` flag distingue la première
  // hydration depuis tenant data (n'active pas dirty) des modifs subséquentes.
  const isHydrated = useRef(false);
  const [dirty, setDirty] = useState(false);

  const onInfoChange = useCallback((next: TenantInfoFormState) => {
    setInfo(next);
    if (isHydrated.current) setDirty(true);
  }, []);
  const onBrandingChange = useCallback((next: Branding | null) => {
    setBranding(next);
    if (isHydrated.current) setDirty(true);
  }, []);
  const onAdminChange = useCallback((next: AdminSettingsFormState) => {
    setAdmin(next);
    if (isHydrated.current) setDirty(true);
  }, []);

  // 9.P.2.R2 + R2 patch Save bypass — appelé AVANT tout early return.
  const { allowNavigation } = useUnsavedChangesGuard(dirty, t("settings.tenant.unsaved_message"));

  // D6.1 — toast RBAC déplacé en effect pour respecter rules-of-hooks.
  useEffect(() => {
    if (user && !isAdminAllowed) {
      toast.error(t("settings.rbac.denied"), { id: "settings-rbac" });
    }
  }, [user, isAdminAllowed, t]);

  // Hydratation depuis AuthContext.
  useEffect(() => {
    if (!tenant) return;
    setInfo({
      name: tenant.name ?? "",
      locale: tenant.locale ?? "fr",
      timezone: tenant.timezone ?? "America/Montreal",
      currency: tenant.currency ?? "CAD",
      logo_url: tenant.logo_url || tenant.branding?.logo_url || "",
    });
    setBranding(tenant.branding ?? null);
    setDeliveryPolicy(tenant.quote_delivery_policy ?? null);
    setDeliveryPolicyDirty(false);
    // Mark hydrated AFTER setters → ne déclenche pas dirty sur l'hydration initiale.
    isHydrated.current = true;
  }, [tenant]);

  // Pour super_admin : charger admin SaaS via GET /v1/tenants/:id.
  useEffect(() => {
    if (!isSuperAdmin || !tenant?.id) {
      setLoadingAdmin(false);
      return;
    }
    void (async () => {
      try {
        const full = await api.get<{
          plan: string;
          status: string;
          trial_ends_at: string | null;
          max_users: number | null;
          max_projects: number | null;
          max_storage_mb: number | null;
        }>(`/v1/tenants/${tenant.id}`);
        setAdmin({
          plan: (full.plan as AdminSettingsFormState["plan"]) ?? "trial",
          status: (full.status as AdminSettingsFormState["status"]) ?? "active",
          trial_ends_at: full.trial_ends_at ?? "",
          max_users: full.max_users ?? 50,
          max_projects: full.max_projects ?? 500,
          max_storage_mb: full.max_storage_mb ?? 10_000,
        });
      } catch {
        setAdmin(null);
      } finally {
        setLoadingAdmin(false);
      }
    })();
  }, [isSuperAdmin, tenant?.id]);

  // Branding live preview.
  useEffect(() => {
    applyBranding(branding);
  }, [branding]);

  // Snapshot pour rollback en cas d'échec PATCH.
  const initialBranding = useMemo(() => tenant?.branding ?? null, [tenant]);

  // RBAC client redirect.
  if (user && !isAdminAllowed) {
    return <Navigate to="/" replace />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!info || !tenant?.id) return;

    setSubmitting(true);
    try {
      const payload =
        isSuperAdmin && admin
          ? buildAdminPayload(info, branding, admin)
          : buildSelfPayload(info, branding);

      if (deliveryPolicy && deliveryPolicyDirty) payload.quote_delivery_policy = deliveryPolicy;
      await api.patch(`/v1/tenants/${tenant.id}`, payload);
      toast.success(t("settings.tenant.saved"));
      // 9.P.2.R2 patch Save bypass — allowNavigation+setDirty(false) avant refresh
      // (le refresh navigue pas mais protège la séquence dirty si user click NavLink juste après save).
      allowNavigation();
      setDirty(false);
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ["quote-requests", "readiness"] });
    } catch (err) {
      applyBranding(initialBranding);
      const msg =
        err instanceof ApiException
          ? getApiErrorMessage(err, lang) || t("settings.tenant.save_failed")
          : t("settings.tenant.save_failed");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function onResetBranding() {
    setBranding(null);
    if (isHydrated.current) setDirty(true);
    toast.info(t("settings.branding.reset_done"));
  }

  // 9.AS.1 — un super_admin SANS cabinet sélectionné n'a rien à éditer ici.
  //
  // Cet écran s'hydrate depuis `tenant` (issu de `/v1/auth/me`), qui vaut `null`
  // pour un super_admin : `info` restait `null` et le rendu retombait sur le
  // squelette ci-dessous, **indéfiniment**. Pas de message, pas d'erreur — un
  // écran qui charge pour toujours, ce qui ressemble à une lenteur et pousse à
  // recharger la page.
  //
  // On dit maintenant ce qui se passe, et on donne les deux gestes qui le lèvent :
  // choisir un cabinet dans la barre du haut, ou en créer un.
  if (!info && isSuperAdmin && !loadingAdmin) {
    return (
      <div className="mx-auto w-full max-w-screen-xl py-8">
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-slate-900">
            {t("tenant_scope.no_scope_title")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">{t("tenant_scope.no_scope_body")}</p>
          <button
            type="button"
            onClick={() => setCreationOuverte(true)}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 mt-5 touch-manipulation rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {t("tenant_scope.create")}
          </button>
        </div>
        {creationOuverte && <CreateTenantDialog onClose={() => setCreationOuverte(false)} />}
      </div>
    );
  }

  if (!info || (isSuperAdmin && loadingAdmin)) {
    return (
      <div className="mx-auto w-full max-w-screen-xl">
        <div className="h-32 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
      </div>
    );
  }

  const sectionNavItems = [
    { anchorId: "info", label: t("settings.tenant.section_info") },
    { anchorId: "branding", label: t("settings.branding.title") },
    ...(deliveryPolicy ? [{ anchorId: "delivery", label: t("delivery.policy_title") }] : []),
    ...(isSuperAdmin && admin ? [{ anchorId: "admin", label: t("settings.admin.title") }] : []),
  ];

  return (
    <div className="mx-auto w-full max-w-screen-xl">
      {/* Sticky header */}
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:top-0 lg:-mx-8 lg:px-8 lg:py-5">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {t("settings.tenant.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{t("settings.tenant.subtitle")}</p>
      </header>

      <form onSubmit={onSubmit} className="py-6 lg:grid lg:grid-cols-[14rem_1fr] lg:gap-8 lg:py-8">
        {/* Section nav left (desktop only) */}
        <aside className="hidden lg:block">
          <div className="sticky top-32">
            <CompanySectionNav
              items={sectionNavItems}
              ariaLabel={t("settings.tenant.section_nav_label")}
            />
          </div>
        </aside>

        {/* Form right column */}
        <div className="space-y-5">
          <FormCard id="info" title={t("settings.tenant.section_info")}>
            <TenantInfoForm
              tenantId={tenant?.id}
              value={info}
              onChange={onInfoChange}
              disabled={submitting}
            />
          </FormCard>

          <FormCard
            id="branding"
            title={t("settings.branding.title")}
            actions={
              <button
                type="button"
                onClick={onResetBranding}
                disabled={submitting}
                className="focus-visible:ring-brand-500 rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {t("settings.branding.reset")}
              </button>
            }
          >
            <BrandingForm value={branding} onChange={onBrandingChange} disabled={submitting} />
          </FormCard>

          {deliveryPolicy && (
            <FormCard id="delivery" title={t("delivery.policy_title")}>
              <p className="mb-4 text-sm text-slate-600">
                {t("delivery.policy_help", { cabinet: tenant?.name })}
              </p>
              <fieldset disabled={submitting} className="space-y-2">
                <legend className="sr-only">{t("delivery.policy_title")}</legend>
                {(["require_census", "include_signed_mandate", "require_commission"] as const).map(
                  (key) => (
                    <label
                      key={key}
                      className="flex min-h-11 cursor-pointer items-center gap-3 text-sm text-slate-800"
                    >
                      <input
                        type="checkbox"
                        className="focus-visible:ring-brand-500 h-4 w-4 rounded focus-visible:ring-2"
                        checked={deliveryPolicy[key]}
                        onChange={(event) => {
                          setDeliveryPolicy({ ...deliveryPolicy, [key]: event.target.checked });
                          setDeliveryPolicyDirty(true);
                          setDirty(true);
                        }}
                      />
                      {t(`delivery.${key}`)}
                    </label>
                  ),
                )}
              </fieldset>
            </FormCard>
          )}

          {isSuperAdmin && admin && (
            <FormCard
              id="admin"
              title={t("settings.admin.title")}
              subtitle={t("settings.admin.subtitle")}
              tone="amber"
            >
              <AdminSettingsForm value={admin} onChange={onAdminChange} disabled={submitting} />
            </FormCard>
          )}

          {/* Sticky save bar */}
          <div className="sticky bottom-0 z-10 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-sm lg:-mx-8 lg:px-8">
            <div className="flex items-center justify-end gap-3">
              <button
                type="submit"
                disabled={submitting}
                aria-busy={submitting}
                className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {submitting ? t("app.loading") : t("common.save")}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

function FormCard({
  id,
  title,
  subtitle,
  actions,
  tone = "default",
  children,
}: {
  id: string;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  tone?: "default" | "amber";
  children: React.ReactNode;
}) {
  const borderClass = tone === "amber" ? "border-amber-200" : "border-slate-200";
  return (
    <section id={id} className={`scroll-mt-32 rounded-xl border ${borderClass} bg-white shadow-sm`}>
      <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          {subtitle && <p className="mt-1 text-xs text-amber-800">{subtitle}</p>}
        </div>
        {actions && <div className="shrink-0">{actions}</div>}
      </header>
      <div className="px-5 py-5">{children}</div>
    </section>
  );
}
